import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getConversations } from '../../services/chats';
import useUser from '../global/useUser';
import useChatFilters from '../../components/chat/useChatFilters';

const LIMIT = 29;

function useGetConversations() {
  const { user } = useUser();
  const { filter } = useChatFilters();

  const queryKey = React.useMemo(
    () => [
      'chat-conversations',
      { userId: user?.userId, businessId: user?.business?.id, filter },
    ],
    [user?.userId, user?.business?.id, filter],
  );

  const {
    data,
    isFetching,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isSuccess,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async ({ pageParam: offsetTs }) => {
      const result = await getConversations({
        businessId: user?.business?.id!,
        limit: LIMIT,
        offsetTs: offsetTs || undefined,
        filter,
      });
      return result;
    },
    enabled: !!user?.userId && !!user?.business?.id,
    initialPageParam: 0,
    getNextPageParam: lastPage => {
      if (!lastPage.meta?.hasMore) return null;
      return lastPage.meta.cursor;
    },
  });

  const allConversations = React.useMemo(
    () => data?.pages?.flatMap(page => page.result) ?? [],
    [data?.pages],
  );

  return {
    conversations: allConversations,
    queryKey,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
  };
}

export default useGetConversations;
